import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FixedWidgets from '../components/FixedWidgets';
import h from '../assets/hospital.png';
import building from '../assets/hospital.png';
import special from '../assets/special.jpeg';
import cori from '../assets/cori.jpeg';
import ot from '../assets/OT.jpeg';
import icu from '../assets/ICU.jpeg';
import ipd from '../assets/ipd.jpeg';
import ward from '../assets/ward.jpeg';
import lab from '../assets/lab.avif';
import x from '../assets/x-ray.png';
import pharm from '../assets/pharm.png';
import ambu from '../assets/ambu.jpeg';
import pooja from '../assets/pooja.jpeg';

const GalleryPage = () => {
  const [selectedImage, setSelectedImage] = useState(null);

  const galleryImages = [
    { id: 1, url: building, title: 'Hospital Building Exterior', category: 'Infrastructure' },
    { id: 12, url: ot, title: 'Operation Theater', category: 'Operations' },
    { id: 4, url: cori, title: 'Spacious Corridors', category: 'Infrastructure' },
    { id: 2, url: special, title: 'Patient Care Room', category: 'Facilities' },
    { id: 5, url: icu, title: 'ICU', category: 'Modern Technology' },
    { id: 6, url: ipd, title: 'IPD', category: 'Pateint Recovery' },
    { id: 7, url: ward, title: ' General Wards', category: 'Pateint Recovery' },
    { id: 8, url: lab, title: 'Laboratory', category: 'Technology' },
    { id: 9, url: x, title: 'X-Ray', category: 'Technology' },
    { id: 11, url: pooja, title: 'Pooja Room', category: 'Infrastructure' },
    { id: 10, url: pharm, title: 'Pharmacy', category: 'Medicine' },
    { id: 11, url: ambu, title: '24/7 Ambulance', category: 'Facilities' },
    
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <FixedWidgets />

      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden mt-20">
        <motion.div
          initial={{ scale: 1.2 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.5 }}
          className="absolute inset-0 z-0"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#1e3a5f]/90 to-[#4A90A4]/80 z-10" />
          <img
            src={h}
            alt="Gallery"
            className="w-full h-full object-cover"
          />
        </motion.div>

        <div className="relative z-20 max-w-7xl mx-auto px-6 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl md:text-6xl font-bold text-white mb-6"
          >
            Our <span className="text-[#6FCF97]">Gallery</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl text-gray-200 max-w-3xl mx-auto"
          >
            Explore our modern facilities and advanced infrastructure
          </motion.p>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {galleryImages.map((image, index) => (
              <motion.div
                key={image.id}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                whileHover={{ scale: 1.05, y: -10 }}
                onClick={() => setSelectedImage(image)}
                className="relative group cursor-pointer rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300"
              >
                <div className="aspect-square overflow-hidden">
                  <motion.img
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.5 }}
                    src={image.url}
                    alt={image.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <p className="text-white font-semibold text-lg mb-1">{image.title}</p>
                    <p className="text-gray-300 text-sm">{image.category}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Image Modal */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedImage(null)}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-5xl bg-white rounded-2xl overflow-hidden shadow-2xl"


            >
              <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 z-50 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-md hover:scale-110 transition"
            >
              <X size={22} className="text-gray-800" />
            </button>


              {/* Image */}
            <div className="w-full h-[75vh] bg-black flex items-center justify-center">
              <img
                src={selectedImage.url}
                alt={selectedImage.title}
                className="max-w-full max-h-full object-contain"
              />
            </div>

            {/* Title & Category BELOW image */}
            <div className="p-6 text-center">
              <h3 className="text-2xl font-bold text-gray-800 mb-2">
                {selectedImage.title}
              </h3>
              <p className="text-gray-500">{selectedImage.category}</p>
            </div>

            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
};

export default GalleryPage;